import type { HTMLAttributes, ReactNode, Ref } from 'react';
import { cx } from '../cx';

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export type CardProps = HTMLAttributes<HTMLElement> & {
  as?: 'div' | 'article' | 'section' | 'li' | 'aside';
  /** Lifts on hover and rings on keyboard focus inside; use when the whole card is one link. */
  interactive?: boolean;
  /** No shadow: for a card inside a card (never nest two shadowed cards). */
  flat?: boolean;
  padding?: CardPadding;
  /** Passed to the element (React 19 ref-as-prop). */
  ref?: Ref<HTMLElement>;
  children: ReactNode;
};

const PADDING: Record<CardPadding, string> = { none: '', sm: 'p-4', md: 'p-5 md:p-6', lg: 'p-6 md:p-8' };

/** The container for grouped content: surface, card radius, a hairline, and a shadow unless flat. */
export function Card({
  as: Tag = 'div',
  interactive = false,
  flat = false,
  padding = 'md',
  className,
  children,
  ref,
  ...rest
}: CardProps) {
  // Every allowed tag is an HTMLElement; typed as div so one ref type fits them all.
  const Element = Tag as 'div';
  return (
    <Element
      {...rest}
      ref={ref as Ref<HTMLDivElement>}
      className={cx(
        'relative rounded-jc-container border border-jc-card-border bg-jc-surface text-jc-ink',
        !flat && 'shadow-jc-sm',
        interactive &&
          'transition duration-(--jc-duration-fast) ease-jc-out hover:-translate-y-0.5 hover:shadow-jc-md focus-within:ring-2 focus-within:ring-jc-focus motion-reduce:transition-none motion-reduce:hover:translate-y-0',
        PADDING[padding],
        className,
      )}
    >
      {children}
    </Element>
  );
}
